// src/data/content-team.js

/**
 * ============================================================================
 * 团队内容 - Team Content
 * ============================================================================
 *
 * 【作用】
 * - 控制“团队成员”页的标题、副标题、招募提示（短句），PI 信息，以及成员卡片列表。
 *
 * 【如何修改】
 * - 页面级文案：title / subtitle / recruitNote
 * - PI 文案与链接：piRole / piTitle / piDesc / piDetails
 * - 增删成员：members 数组中增删对象
 * - 图片资源：放在 public/images/team/，路径以 /images/team/... 引用
 *
 * 【新增说明｜招募提示 recruitNote】（第三人称客观注释）
 * - recruitNote 用于“招生友好”的一行短句，建议保持克制与具体：
 *   - 英文建议 1 句；中文建议 1 句；
 *   - 避免过度承诺（如 guarantee / breakthrough 等），以“欢迎联系/欢迎 rotation”更稳健。
 *
 * 【成员对象参数说明】（第三人称客观注释）
 * - name: string，显示名（建议唯一；若可能重名，建议加入 id 字段并作为 React key）
 * - role / focus: { en: string, cn: string }，卡面显示的职位与研究主题
 * - color: string，卡片边框颜色 Tailwind 类，如 "border-pink-500/50"
 * - details: object，详情弹窗所需字段（建议 en/cn 都提供）
 *   - bio: string，简介（1~3 段皆可）
 *   - education: string，可选
 *   - research: string，可选
 *   - publications: string，可选（可写“代表作/数量/链接说明”等）
 *   - email: string，可选（用于 mailto）
 *   - image: string，可选，成员照片路径（/images/team/xxx.jpg）
 *   - scholar: string，可选，Google Scholar 链接（用于按钮/外链）
 *
 * 【PI 字段说明】（第三人称客观注释）
 * - piDetails：同样建议包含 scholar（Google Scholar）与 website（个人主页）
 * - 若 website 与 scholar 相同，可只写 scholar，并将 website 留空；页面会自动按存在字段渲染。
 */

export const teamContent = {
  // ############################################################################
  // ############## 01. PAGE HEADER（页面标题区） ################################
  // ############################################################################
  title: {
    en: "The Team",
    cn: "团队成员",
  },

  subtitle: {
    en: "Chemists, physicists, and computer scientists working under one roof.",
    cn: "化学家、物理学家和计算机科学家，在同一个屋檐下协作。",
  },

  /**
   * 招募提示：一行短句（招生友好）
   * - 建议表达清晰、具体、克制
   * - 若未来需要更明确的招聘入口，可在 contact 页面或 footer 中放置申请链接
   */
  recruitNote: {
    en: "We welcome rotations, visiting students, visiting scholars, and motivated applicants. Feel free to reach out via the Contact page.",
    cn: "欢迎对本组方向感兴趣的学生/访问学者交流（rotation/访问/申请均可），可通过 「联系我们」 页面联系。",
  },

  // ############################################################################
  // ############## 02. PI（课题组长） ###########################################
  // ############################################################################
  piRole: {
    en: "Principal Investigator",
    cn: "课题组长（PI）",
  },

  piTitle: {
    en: "Prof. Boyu Qie",
    cn: "郄博宇",
  },

  /**
   * PI 卡片摘要（用于 roster 卡面）
   * - 建议 1~2 句；完整 bio 放到 piDetails.bio
   */
  piDesc: {
    en: "Leading the convergence of organic chemistry and quantum physics.",
    cn: "致力于融合有机化学与量子物理，构筑自然界不存在的人造量子材料。",
  },

  /**
   * PI 详情（用于 Modal）
   * - scholar：Google Scholar 链接（用于按钮）
   * - website：个人主页（可选）
   */
  piDetails: {
    en: {
      bio: `Boyu Qie is an incoming associate professor at **Southern University of Science and Technology (SUSTech)**, Shenzhen, China, starting from June 2026. He was born and raised in Chengdu, China, and obtained his B.S. degree with *Summa Cum Laude* from the first Experimental Reform Class at SUSTech (SUSTC then), followed by an M.S. in Materials Science from **Columbia University**. He earned his Ph.D. in Chemistry from **University of California, Berkeley**, where he worked with Prof. Felix R. Fischer, with co-mentorship from Prof. Michael F. Crommie and Prof. Steven G. Louie. He subsequently received the Postdoctoral Fellowship from **BIDMaP Institute** (Bakar Institute of Digital Materials for the Planet) at UC Berkeley, working with Prof. Aditi S. Krishnapriyan (Computer Science) and Prof. Felix R. Fischer (Chemistry). 

🤖 His current research interests lie at the intersection of *synthetic chemistry*, *condensed matter physics*, *electronic devices*, and *artificial intelligence*. His group aims to develop novel quantum materials and electronic applications through interdisciplinary approaches, as well as explore data-driven paradigms for materials discovery.

👋 Students and collaborators interested in related topics are warmly welcome to get in touch and discuss!`,
      focus: "Leading the convergence of organic chemistry and quantum physics.",
      education: `**BIDMaP Postdoc Fellow**: University of California, Berkeley, BIDMaP, USA, *2024 - 2026*

🧑‍🎓 Co-advisors: Prof. Aditi S. Krishnapriyan, Prof. Felix R. Fischer

**Ph.D. in Chemistry**: University of California, Berkeley, USA, *2019 - 2024*

🧑‍🎓 Advisor: Prof. Felix R. Fischer; Co-advisors: Prof. Michael F. Crommie, Prof. Steven G. Louie

**M.S. in Materials Science**: Columbia University, USA, *2016 - 2018 (RA 2018-2019)*

🧑‍🎓 Advisor: Prof. Yuan Yang; Co-advisor: Prof. Xavier Roy

**B.S. in Chemistry**: South University of Science and Technology of China (SUSTC), China, *2011 - 2015 (RA 2015-2016)*

🧑‍🎓 First Experimental Reform Class (*2011*), *Summa Cum Laude*
`,
      research: `🔬	1. Exploration of novel quantum matter by combining *chemical synthesis* with condensed matter physics (CMP).

🔬	2. Investigation of emerging exotic quantum phenomena in novel CMP systems via *various probing methods* (e.g., STM/STS, ARPES, transport).

🔬	3. Next-generation materials and quantum architectures for *beyond-CMOS electronics/spintronics/quantum tech*.

🔬	4. *Theoretical development and ab initio simulation* for chemical quantum systems.

🔬	5. *Artificial intelligence (AI)* for chemical and physical science.
`,
      awardsFunding: `4.	*2024 – 2026*, BIDMaP Emerging Scholar Postdoc Fellowship，UC Berkeley BIDMaP

	“AI for Low-Dimensional Carbon Materials Library”        

3.	*2024 – 2026*, NERSC Director’s Discretionary Reserve Award, DOE NERSC, **20000 CPU + 2500 GPU node hours**

	“High-throughput ML/DFT conjugated carbon library for organic quantum materials”

2.	*2025 – 2026*, NERSC High-impact Science at Scale Award, DOE NERSC, **30000 CPU + 1000 GPU node hours**

	“High-throughput ML/DFT conjugated carbon library for organic quantum materials”

1.	*2023 – 2024*, UC Berkeley Kavli ENSI Energy NanoScience Institute (ENSI) Fellowship, Kavli Foundation, **$60,000.00**

	“Engineering non-trivial Topological Flat Bands in Lower-Dimensional Lattices”
  
`,
      email: "boyuqie@berkeley.edu",
      scholar: "https://scholar.google.com/citations?user=aAqWTyUAAAAJ&hl=en",
      website: "https://www.boyuqie.com",
      image: "/images/team/boyuqie.jpg",
    },
    cn: {
      bio: `➡️郄博宇即将入职（2026年6月）深圳**南方科技大学**化学系副教授，他出生与成长于四川成都。本科毕业于南方科技大学第一届教改实验班，后在美国**哥伦比亚大学**获得材料科学硕士学位，并且在**加州大学伯克利分校**获得化学博士学位，博士导师为Felix R. Fischer教授，合作导师为物理系Michael F. Crommie和Steven G. Louie教授。此后获得**加州大学伯克利分校BIDMaP (Bakar Institute of Digital Materials for the Planet)** 研究所博士后Fellowship研究基金，合作导师为计算机系Aditi S. Krishnapriyan教授与化学系Felix R. Fischer教授。

🤖他的研究兴趣目前聚焦于*合成化学*、*凝聚态物理*、*电子器件*和*人工智能*之间的交叉领域，希望通过跨学科方法开发新型量子材料与电子应用，并探索数据驱动的材料研究新范式。

👋非常欢迎对相关研究方向感兴趣的学生与合作者联系讨论与交流！
------------------------------------------------------------------------------------------------`,
      focus: "致力于融合有机化学与量子物理，构筑自然界不存在的人造量子材料。",
      education: `- **博士后**：加州大学伯克利分校，BIDMaP Emerging Scholar Fellowship，美国，*2024 - 2026*

🧑‍🎓 合作导师：Aditi S. Krishnapriyan教授, Felix R. Fischer教授

- **博士**：加州大学伯克利分校，化学系，美国，*2019 - 2024*

🧑‍🎓 导师：Felix R. Fischer教授，合作导师：Michael F. Crommie教授, Steven G. Louie教授

- **硕士**：哥伦比亚大学，材料科学和工程系，美国，*2016 - 2018年 （研究助理 2018-2019）*

🧑‍🎓 导师：Yuan Yang教授，合作导师：Xavier Roy教授

- **本科**：南方科技大学，化学系，中国，*2011 - 2015 （研究助理 2015-2016）*

🧑‍🎓 第一届教改实验班（*2011年*），*Summa Cum Laude*
`,
      research: `🔬 1. 通过结合*化学合成*与凝聚态物理（CMP）探索新型量子物质。

🔬 2. 利用多种*前沿表征手段*（如 STM/STS、ARPES、输运等）研究新颖CMP体系中的奇异量子现象。

🔬 3. 面向*后CMOS时代电子器件/自旋电子学/量子技术*的新一代材料和量子结构设计。

🔬 4. 化学量子体系的*理论发展与从头计算模拟*。

🔬 5. *人工智能（AI）* 在化学与物理科学中的研究和应用。
`,
      awardsFunding: `***项目资助***

5.	*2026 – 2029*, 国家自然科学基金优秀青年科学基金项目（海外）

4.	*2024 – 2026*, 加州大学伯克利分校BIDMaP博士后奖学金，BIDMaP研究所

	“AI for Low-Dimensional Carbon Materials Library”        

3.	*2024 – 2026*, 美国能源部/能源研究计算科学中心, NERSC Directors Discretionary Reserve Award, **20,000 CPU + 2,500 GPU node hours**

	“High-throughput ML/DFT conjugated carbon library for organic quantum materials”

2.	*2025 – 2026*, 美国能源部/能源研究计算科学中心, NERSC High-impact Science at Scale Award, **30,000 CPU + 1,000 GPU node hours**

	“High-throughput ML/DFT conjugated carbon library for organic quantum materials”

1.	*2023 – 2024*, 加州大学伯克利分校Kavli ENSI能源纳米科学研究所(ENSI)奖学金, 卡弗里基金会, **$60,000.00**

	“Engineering non-trivial Topological Flat Bands in Lower-Dimensional Lattices”
  
------------------------------------------------------------------------------------------------
***部分近期获奖与荣誉***

3. *2024*, 加州大学伯克利分校-巴卡尔地球数字材料研究所BIDMAP-新锐学者研究基金

2. *2023*, 剑桥大学-加州大学伯克利分校Winton-Kavli项目最佳研究计划奖

1. *2023*, 加州大学伯克利分校-卡弗里能源纳米研究所Kavli ENSI-博士奖学金
`,
      email: "boyuqie@berkeley.edu",
      scholar: "https://scholar.google.com/citations?user=aAqWTyUAAAAJ&hl=en",
      website: "https://www.boyuqie.com",
      image: "/images/team/boyuqie.jpg",
    },
  },


  // ############################################################################
  // ############## 03. BUTTON LABELS（按钮文字） ################################
  // ############################################################################
  buttons: {
    scholar: { en: "Google Scholar", cn: "Google Scholar" },
    cv: { en: "CV", cn: "简历" },
  },

  // ############################################################################
  // ############## 04. MEMBERS（团队成员列表） ##################################
  // ############################################################################
  members: [
    {
      // [建议] 若未来可能出现重名，建议新增 id 字段（如 "emily-chen"）并作为 React key
      name: { en: "Mingchen Gao", cn: "高铭辰" },
      role: { en: "PhD Student - 2026", cn: "博士研究生 - 2026" },
      focus: { en: "Organic Quantum Materials Theory, AI for Science, Database", cn: "有机量子材料理论，人工智能，数据库" },
      color: "border-cyan-500/50",

      details: {
        en: {
          bio: `Mingchen Gao is an incoming PhD student in the Department of Chemistry at **Southern University of Science and Technology (SUSTech)**, where he will be advised by Prof. Boyu Qie. He graduated from **Southeast University** with a B.S. in Applied Physics and obtained his M.S. in Materials Science and Engineering from **SUSTech**, advised by Prof. Dian Tan. 
          His research interests include the theory of organic quantum materials, AI for science, and database.`,
          education: `*B.S. in Applied Physics*, Southeast University, China, *2016 - 2020*

*M.S. in Materials Science*, SUSTech, China, *2020 - 2022*

🧑‍🎓 Advisor: Prof. Dian Tan
`,
          research: "Organic quantum materials theory, AI for science, database",
          // publications: "此处可填写成员发表概况，如 15+ papers in peer-reviewed journals",
          email: "2074093249@qq.com",
          // scholar: "此处可填写 Google Scholar 链接",
          image: "/images/team/mingchengao.jpg",
        },
        cn: {
          bio: `高铭辰即将就读深圳**南方科技大学**化学系博士研究生（2026年9月）。他本科毕业于**东南大学**应用物理学专业，后在**南方科技大学**获得材料与化工硕士学位，硕士导师为谭电教授。
          他目前的研究兴趣包括有机量子材料理论、人工智能在科学中的应用和数据库。
          `,
          education: `
**硕士，材料与化工**，南方科技大学，*2020 - 2022*

🧑‍🎓 导师：谭电教授

**学士，应用物理学**，东南大学，*2016 - 2020*
`,
          research: "有机量子材料理论，人工智能，数据库",
          // publications: "此处可填写成员发表概况（中文）",
          email: "2074093249@qq.com",
          // scholar: "此处可填写 Google Scholar 链接",
          image: "/images/team/mingchengao.jpg",
        },
      },
    },
    {
      name: { en: "You are the next one! Join us!", cn: "希望你成为团队的下一个成员！"},
      role: { en: "Join Us", cn: "欢迎加入我们！" },
      focus: { en: "Let's explore the future of science together!", cn: "让我们一起探索发展未来的科学领域" },
      color: "border-gray-500/50",
      details: {
        en: {
          bio: "We welcome anyone interested in joining our team! With enthusiasm and dedication, let's do some exciting science together! If you are either interested in our field of organic quantum materials or our field of chemical quantum systems, or just want to chat and explore the future of science, please contact us via email: boyuqie@berkeley.edu",
          // education: "此处可填写教育经历（英文）",
          research: "Let's contribute to the new field of organic quantum materials and establishing chemical quantum systems! We are looking for talented researchers with background among chemistry, physics, electrical engineering, computer science, and materials science to join our team. ",
          // publications: "此处可填写发表/预印本说明（英文）",
          email: "please email to boyuqie@berkeley.edu",
          // scholar: "此处可填写 Google Scholar 链接",
          image: "/images/team/joinus.png",
        },
        cn: {
          bio: "我们欢迎任何对科学感兴趣的人加入我们的团队！怀着热情和奉献精神，让我们一起做些激动人心的科学吧！如果你对有机量子材料或化学量子系统感兴趣，或者是想聊聊科研，探索未来的科学发展可能性，都欢迎通过邮件与我们联系：boyuqie@berkeley.edu",
          // education: "此处可填写教育经历（中文）",
          research: "让我们一起为有机量子材料和化学量子系统的新领域做出贡献！我们正在寻找包括但不限于化学、物理、电气工程、计算机科学和材料科学背景的有才华的研究人员加入我们的团队。",
          // publications: "此处可填写发表/预印本说明（中文）",
          email: "请发送邮件至 boyuqie@berkeley.edu",
          // scholar: "此处可填写 Google Scholar 链接",
          image: "/images/team/joinus.png",
        },
      },
    },
    // 此处可继续添加更多成员（复制上方对象结构填写）
  ],
};

/**
 * ============================================================================
 * 团队成员模板（复制后按需填充）
 * ============================================================================
 *
 * 【填写建议】（第三人称客观注释）
 * - name：建议与照片文件名形成可追踪对应（例如 marcus-thorne.jpg）
 * - role/focus：卡面显示信息，尽量短而清晰
 * - bio：可写 2~5 句，偏“对外介绍”
 * - publications：建议用“代表作/数量/领域”描述，避免夸张表述
 * - scholar：若没有可留空或删除该字段；页面会自动不渲染按钮
 */

export const TEAM_MEMBER_TEMPLATE = {
  name: "Marcus Thorne",
  role: { en: "PhD Candidate", cn: "博士研究生" },
  focus: { en: "Spin Liquids", cn: "自旋液体" },
  color: "border-cyan-500/50",
  details: {
    en: {
      bio: "Marcus investigates spin-liquid states in organic quantum materials using computational and experimental approaches.",
      education: "M.Sc. in Physics, Stanford University",
      research: "Spin liquids, quantum magnetism, computational physics",
      publications: "Selected works and preprints available on Google Scholar.",
      email: "m.thorne@university.edu",
      scholar: "https://scholar.google.com/...",
      image: "/images/team/marcus.jpg",
    },
    cn: {
      bio: "Marcus 使用计算与实验方法研究有机量子材料中的自旋液体态。",
      education: "斯坦福大学 物理学硕士",
      research: "自旋液体、量子磁性、计算物理",
      publications: "代表作与预印本可见 Google Scholar。",
      email: "m.thorne@university.edu",
      scholar: "https://scholar.google.com/...",
      image: "/images/team/marcus.jpg",
    },
  },
};
